import useResizeObserver from "@react-hook/resize-observer";
import * as Sentry from "@sentry/react";
import { FC, ReactNode, useEffect, useRef } from "react";

import { QuestionCircleOutlined } from "@ant-design/icons";
import FullScreenIcon from "@material-ui/icons/Fullscreen";
import FullscreenExitIcon from "@material-ui/icons/FullscreenExit";
import { Button, Card, Tooltip } from "antd";
import { EChartsOption } from "echarts";
import * as echarts from "hd-echarts/echarts";
import styled from "styled-components";
import { useDebouncedCallback } from "use-debounce";
import { arrayOrEmpty } from "utils";

import { getDecoratorsForType } from "./charts/decorators/decorator";
import { EChartsTransformer } from "./charts/transformer";
import {
  ChartContext,
  FeatureImportanceSummary,
  MlChartType,
  MlTarget,
  WellGroupInfo
} from "./types";

export interface FeatureImportanceChartProps {
  key: string;
  chartType: MlChartType;
  data?: FeatureImportanceSummary;
  title: (target: MlTarget, summary: FeatureImportanceSummary) => string | ReactNode;
  description: string;
  transformer: EChartsTransformer<FeatureImportanceSummary>;
  mapWellEntities?: WellGroupInfo;
  target?: MlTarget;
  context?: ChartContext;
  onWellHover?: (well: string | null) => void;
  onChartFullscreen?: (chartKey: string | null) => void;
  isFullscreen?: boolean;
  height?: string;
}

const CardContainer = styled(Card)<{ $isFullscreen?: boolean }>`
  width: 100%;
  padding: 0;
  min-height: 400px;

  .ant-card-body {
    padding: 5px;
    min-height: 340px;
  }

  ${(props) =>
    props.$isFullscreen &&
    `
    height: calc(100vh - 45px - 48px); //subtract nav bar height, and container padding
    display: flex;
    flex-direction: column;
    min-height: unset;

    .ant-card-body {
      flex: 1;
      overflow: hidden;
      min-height: unset;
    }

    @media screen and (max-width: 1600px) {
      height: calc(100vh - 45px - 20px); //subtract nav bar height, and container padding
    }
  `}
`;

const CardHeaderContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 68px;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
`;

const CardHeaderTitle = styled.div`
  cursor: default;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CardInfoContainer = styled.div`
  display: flex;
  gap: 4px;
`;

const ChartWrapper = styled(Card)<{ $isFullscreen?: boolean }>`
  width: 100%;
  height: 100%;
  padding: 0;

  .ant-card-body {
    padding: 8px;
    height: 100%;
  }
`;

const NoDataMessage = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 3;

  width: 90%;
  max-width: 330px;
  color: #4b4b4b;
  background-color: rgb(186, 189, 194);
  padding: 2px 10px;
  border-radius: 5px;
  text-align: center;
`;

export const FeatureImportanceChart: FC<FeatureImportanceChartProps> = ({
  title,
  key,
  chartType,
  description,
  transformer,
  data,
  mapWellEntities,
  target,
  context,
  onWellHover,
  onChartFullscreen,
  isFullscreen,
  height = "350px"
}) => {
  const prevOptionRef = useRef<EChartsOption>(null);
  const chartDomRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<echarts.ECharts>(null);
  const onWellHoverRef = useRef(onWellHover);
  const dataRef = useRef<FeatureImportanceSummary>(data);
  const debouncedHoveredWell = useDebouncedCallback(
    (well: string) => {
      onWellHoverRef.current(well);
    },
    200,
    { leading: false, trailing: true }
  );
  const dpr = window.devicePixelRatio || 1;
  const width = chartInstance.current?.getWidth() * dpr;

  useEffect(() => {
    onWellHoverRef.current = onWellHover;
  }, [onWellHover]);

  useEffect(() => {
    if (chartDomRef.current && !chartInstance.current) {
      chartInstance.current = echarts.init(chartDomRef.current);
      chartInstance.current.on(
        "hover",
        (e: { seriesIndex: number; dataIndex: number }) => {
          const option = chartInstance.current.getOption();
          const series = option.series[e.seriesIndex] as { name: string; type: string };

          if (!series?.name || series?.type != "line") {
            return;
          }

          debouncedHoveredWell(series.name);
        }
      );
      const zr = chartInstance.current.getZr();
      zr.on("mouseout", () => {
        setTimeout(() => {
          chartInstance.current?.dispatchAction({
            type: "hideTip"
          });
        }, 500);
      });

      chartInstance.current.on(
        "mouseout",
        (params: { seriesName: string; seriesType: "line" | "scatter" }) => {
          if (params.seriesType === "line") {
            onWellHoverRef.current?.(null);
          }
        }
      );
      const handleMouseLeave = () => {
        debouncedHoveredWell.cancel();
        onWellHoverRef.current?.(null);
      };
      // Add mouseleave event to clear hover when mouse leaves the chart area
      const chartDom = chartDomRef.current;

      chartDom.addEventListener("mouseleave", handleMouseLeave);

      // Clean up
      return () => {
        zr?.off("mouseout");
        chartInstance.current?.off("hover");
        chartInstance.current?.off("mouseout");
        chartInstance.current?.dispose();
        if (chartDom) {
          chartDom.removeEventListener("mouseleave", handleMouseLeave);
        }
        chartInstance.current = null;
        chartDomRef.current = null;
      };
    }
  }, []);

  useEffect(() => {
    if (!chartInstance.current || !data) {
      return;
    }

    // Defensive check: ensure data has valid structure before transforming
    // This prevents crashes when data exists but has undefined properties
    try {
      const dataDifferent = dataRef.current !== data;
      dataRef.current = data;
      const baseOption = transformer.transform(
        data,
        mapWellEntities,
        { ...context, isFullscreen },
        width,
        target
      );

      // If transformer returns empty object, skip update
      if (!baseOption || Object.keys(baseOption).length === 0) {
        return;
      }

      const decorators = getDecoratorsForType(chartType);
      const option = decorators.reduce(
        (option, decorator) => decorator.decorate(option, { ...context }),
        baseOption
      );
      const hasDifferentSeries =
        prevOptionRef.current &&
        arrayOrEmpty(prevOptionRef.current?.series as []).length !==
          arrayOrEmpty(option?.series as []).length;
      prevOptionRef.current = option;
      chartInstance.current?.setOption(option, hasDifferentSeries || dataDifferent);
    } catch (error) {
      Sentry.captureException(error);
    }
  }, [
    data,
    transformer,
    mapWellEntities,
    chartType,
    context,
    context?.selectedFeature,
    context?.interactionFeature,
    width,
    target,
    isFullscreen
  ]);

  useResizeObserver(chartDomRef.current, () => {
    chartInstance.current?.resize();
  });

  const titleString =
    typeof title(target, data) === "string" ? title(target, data) : undefined;

  return (
    <CardContainer
      key={key}
      $isFullscreen={isFullscreen}
      title={
        <CardHeaderContent>
          <CardHeaderTitle title={titleString}>{title(target, data)}</CardHeaderTitle>

          <CardInfoContainer>
            <Tooltip title={description}>
              <Button type="text" shape="circle" icon={<QuestionCircleOutlined />} />
            </Tooltip>
            {onChartFullscreen && (
              <Tooltip title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}>
                <Button
                  type="text"
                  shape="circle"
                  icon={
                    isFullscreen ? (
                      <FullscreenExitIcon style={{ fontSize: "20px" }} />
                    ) : (
                      <FullScreenIcon style={{ fontSize: "20px" }} />
                    )
                  }
                  onClick={() => onChartFullscreen(isFullscreen ? null : chartType)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                  }}
                />
              </Tooltip>
            )}
          </CardInfoContainer>
        </CardHeaderContent>
      }>
      <ChartWrapper $isFullscreen={isFullscreen}>
        {!data && <NoDataMessage>Click Calculate to Display Chart</NoDataMessage>}
        <div
          ref={chartDomRef}
          style={{
            width: "100%",
            height: isFullscreen ? "100%" : height,
            minHeight: isFullscreen ? "100%" : height,
            visibility: !data ? "hidden" : "visible"
          }}></div>
      </ChartWrapper>
    </CardContainer>
  );
};
